// Referral graph tile — FE-SCRM-058 (BE-SCRM-025).
//
// Three sub-tables side by side: top accounts / top sales contacts /
// top carrier contacts. Each row shows the referrer's name, prospect
// count, conversion rate, and pipeline value (cents → USD).
//
// Per spec, the page-level period selector (in ``ReportsPanel``)
// cascades the period down here.

import { Card, Table, TBody, THead, Th, Td, Tr } from "@/design-system";
import {
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
} from "@/features/dashboard";

import { useReferralSourcesQuery } from "./hooks";
import { PERIOD_LABEL, formatCents, formatPercent } from "./labels";
import type {
  ReferralSourcesResponse,
  ReportPeriod,
} from "./types";


export interface ReferralGraphTileProps {
  period: ReportPeriod;
}


export function ReferralGraphTile({ period }: ReferralGraphTileProps) {
  const query = useReferralSourcesQuery(period);

  return (
    <Card
      data-testid="referral-graph-tile"
      data-section="referral-graph"
      className="flex flex-col gap-3"
    >
      <header className="flex items-start justify-between gap-2">
        <div>
          <h3 className="text-md font-semibold text-text-primary">
            Referral graph
          </h3>
          <p className="mt-0.5 text-xs text-text-secondary">
            Where new prospects are coming from — and which referrers
            turn introductions into wins.
          </p>
        </div>
        <span className="rounded-full bg-recessed px-2 py-0.5 text-[10px] uppercase tracking-wide text-text-tertiary">
          {PERIOD_LABEL[period]}
        </span>
      </header>

      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={3} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load referral graph"
          retry={() => query.refetch()}
        />
      ) : query.data ? (
        <ReferralBody data={query.data} />
      ) : null}
    </Card>
  );
}


function ReferralBody({ data }: { data: ReferralSourcesResponse }) {
  const empty = (
    data.top_referring_accounts.length === 0
    && data.top_referring_sales_contacts.length === 0
    && data.top_referring_carrier_contacts.length === 0
  );
  if (empty) {
    return (
      <div data-testid="referral-empty-state">
        <DashboardEmptyState
          title="No referrers in this window"
          description={
            "Once prospects come in via account introductions, sales "
            + "contacts, or carrier reps, their conversion ratios will "
            + "show up here."
          }
        />
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 gap-3 lg:grid-cols-3">
      <ReferrerTable
        testId="referral-accounts-table"
        title="Top referring accounts"
        rows={data.top_referring_accounts.map((r) => ({
          key: r.account_id,
          label: r.account_name ?? "(unnamed)",
          sublabel: null,
          count: r.prospect_count,
          conversion: r.conversion_rate,
          pipelineCents: r.pipeline_value_cents,
        }))}
      />
      <ReferrerTable
        testId="referral-sales-contacts-table"
        title="Top referring contacts"
        rows={data.top_referring_sales_contacts.map((r) => ({
          key: r.contact_id,
          label: r.contact_name,
          sublabel: null,
          count: r.prospect_count,
          conversion: r.conversion_rate,
          pipelineCents: r.pipeline_value_cents,
        }))}
      />
      <ReferrerTable
        testId="referral-carrier-contacts-table"
        title="Top referring carrier reps"
        rows={data.top_referring_carrier_contacts.map((r) => ({
          key: r.carrier_contact_id,
          label: r.contact_name,
          sublabel: r.carrier_name,
          count: r.prospect_count,
          conversion: r.conversion_rate,
          pipelineCents: r.pipeline_value_cents,
        }))}
      />
    </div>
  );
}


interface ReferrerRow {
  key: string;
  label: string;
  sublabel: string | null;
  count: number;
  conversion: number;
  pipelineCents: number;
}


function ReferrerTable({
  testId, title, rows,
}: { testId: string; title: string; rows: ReferrerRow[] }) {
  return (
    <div
      data-testid={testId}
      className="overflow-hidden rounded-md border border-border-subtle"
    >
      <div className="bg-recessed px-2 py-1.5 text-xs font-medium text-text-secondary">
        {title}
      </div>
      {rows.length === 0 ? (
        <div className="px-2 py-3 text-xs text-text-tertiary">
          No referrers in this category yet.
        </div>
      ) : (
        <Table density="xs" cellPadding="tight">
          <THead className="text-text-tertiary">
            <Tr>
              <Th>Referrer</Th>
              <Th>Prospects</Th>
              <Th>Won</Th>
              <Th>Pipeline</Th>
            </Tr>
          </THead>
          <TBody>
            {rows.map((row) => (
              <Tr
                key={row.key}
                data-testid={`referral-row-${row.key}`}
                className="border-t border-border-subtle"
              >
                <Td className="text-text-primary">
                  <div>{row.label}</div>
                  {row.sublabel && (
                    <div className="text-text-tertiary text-[10px]">
                      {row.sublabel}
                    </div>
                  )}
                </Td>
                <Td className="text-text-secondary">
                  {row.count}
                </Td>
                <Td className="text-text-secondary">
                  {formatPercent(row.conversion)}
                </Td>
                <Td className="text-text-secondary">
                  {formatCents(row.pipelineCents)}
                </Td>
              </Tr>
            ))}
          </TBody>
        </Table>
      )}
    </div>
  );
}
